import React, { useState, useEffect } from 'react';
import { useTutorial } from '../contexts/TutorialContext';
import { ALL_TUTORIALS, TutorialDefinition, getTutorialsByCategory } from '../config/tutorials';
import './TutorialLibrary.css';

interface TutorialProgress {
  tutorialKey: string;
  status: string;
  lastCompletedStep: number;
  totalSteps: number;
  completedAt?: string;
}

const TutorialLibrary: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { startTutorial, getAllProgress } = useTutorial();
  const [tutorialProgress, setTutorialProgress] = useState<TutorialProgress[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [filteredTutorials, setFilteredTutorials] = useState<TutorialDefinition[]>(ALL_TUTORIALS);

  // Load tutorial progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      const progress = await getAllProgress();
      console.log('Tutorial Library - Progress loaded:', progress);
      console.log('Tutorial Library - Progress count:', progress?.length);
      setTutorialProgress(progress);
    };
    loadProgress();
  }, [getAllProgress]);

  // Filter tutorials based on search and filters
  useEffect(() => {
    let filtered = ALL_TUTORIALS;

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => {
        const progress = tutorialProgress.find(p => p.tutorialKey === t.key);
        console.log(`Tutorial ${t.key} - Progress:`, progress);
        if (statusFilter === 'completed') {
          return progress?.status === 'Completed';
        } else if (statusFilter === 'in-progress') {
          return progress?.status === 'InProgress';
        } else if (statusFilter === 'not-started') {
          return !progress || progress.status === 'NotStarted';
        }
        return true;
      });
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
      );
    }

    setFilteredTutorials(filtered);
  }, [searchQuery, categoryFilter, statusFilter, tutorialProgress]);

  const getTutorialStatus = (tutorialKey: string): TutorialProgress | undefined => {
    return tutorialProgress.find(p => p.tutorialKey === tutorialKey);
  };

  const getStatusBadge = (tutorialKey: string) => {
    const progress = getTutorialStatus(tutorialKey);
    if (!progress || progress.status === 'NotStarted') {
      return <span className="status-badge not-started">Not Started</span>;
    } else if (progress.status === 'InProgress') {
      // lastCompletedStep is 0-based index, so add 1 to show as count
      const stepsCompleted = progress.lastCompletedStep + 1;
      return <span className="status-badge in-progress">In Progress ({stepsCompleted}/{progress.totalSteps})</span>;
    } else if (progress.status === 'Completed') {
      return <span className="status-badge completed">✓ Completed</span>;
    } else if (progress.status === 'Skipped') {
      return <span className="status-badge skipped">Skipped</span>;
    }
    return null;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#f44336';
      case 'high': return '#ff9800';
      case 'medium': return '#4caf50';
      case 'low': return '#2196f3';
      default: return '#888';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'general': return '📖';
      case 'combat': return '⚔️';
      case 'progression': return '🌳';
      case 'exploration': return '🗺️';
      case 'crafting': return '⚒️';
      default: return '❓';
    }
  };

  const handleStartTutorial = (tutorialKey: string) => {
    startTutorial(tutorialKey);
    onClose();
  };

  const completedCount = tutorialProgress.filter(p => p.status === 'Completed').length;
  const completionPercentage = Math.round((completedCount / ALL_TUTORIALS.length) * 100);

  return (
    <div className="tutorial-library-overlay">
      <div className="tutorial-library-modal">
        <div className="tutorial-library-header">
          <h2>📚 Tutorial Library</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Stats Summary */}
        <div className="tutorial-stats">
          <div className="stat-item">
            <span className="stat-label">Total Tutorials:</span>
            <span className="stat-value">{ALL_TUTORIALS.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Completed:</span>
            <span className="stat-value">{completedCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Progress:</span>
            <span className="stat-value">{completionPercentage}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${completionPercentage}%` }} />
          </div>
        </div>

        {/* Search and Filters */}
        <div className="tutorial-filters">
          <input
            type="text"
            placeholder="🔍 Search tutorials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />

          <div className="filter-group">
            <label>Category:</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              <option value="general">📖 General</option>
              <option value="combat">⚔️ Combat</option>
              <option value="progression">🌳 Progression</option>
              <option value="exploration">🗺️ Exploration</option>
              <option value="crafting">⚒️ Crafting</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Tutorial List */}
        <div className="tutorial-list">
          {filteredTutorials.length === 0 ? (
            <div className="no-tutorials">
              <p>No tutorials match your search criteria.</p>
            </div>
          ) : (
            filteredTutorials.map(tutorial => {
              const progress = getTutorialStatus(tutorial.key);
              const isCompleted = progress?.status === 'Completed';

              return (
                <div key={tutorial.key} className={`tutorial-card ${isCompleted ? 'completed' : ''}`}>
                  <div className="tutorial-card-header">
                    <div className="tutorial-title-row">
                      <span className="tutorial-icon">{tutorial.icon}</span>
                      <h3>{tutorial.title}</h3>
                      {getStatusBadge(tutorial.key)}
                    </div>
                    <div className="tutorial-meta">
                      <span className="category-badge">
                        {getCategoryIcon(tutorial.category)} {tutorial.category}
                      </span>
                      <span className="priority-badge" style={{ color: getPriorityColor(tutorial.priority) }}>
                        {tutorial.priority}
                      </span>
                      <span className="duration-badge">
                        ⏱️ {tutorial.estimatedMinutes} min
                      </span>
                    </div>
                  </div>

                  <p className="tutorial-description">{tutorial.description}</p>

                  <div className="tutorial-card-footer">
                    <div className="tutorial-info">
                      <span className="step-count">{tutorial.steps.length} steps</span>
                      {tutorial.prerequisites && tutorial.prerequisites.length > 0 && (
                        <span className="prerequisites">
                          Prerequisites: {tutorial.prerequisites.length}
                        </span>
                      )}
                    </div>
                    <button
                      className="start-tutorial-btn"
                      onClick={() => handleStartTutorial(tutorial.key)}
                    >
                      {isCompleted ? '🔄 Replay' : progress?.status === 'InProgress' ? '▶️ Continue' : '▶️ Start'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialLibrary;
